import { useRef, useState, useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ExternalLinkIcon, LayersIcon, UploadIcon, UploadCloudIcon, Loader2, CheckCircle2, AlertTriangle, SendIcon, Trash2Icon } from 'lucide-react'
import EntityModel, { EntityModelReplaceState } from './EntityModel'
import SdlMetadataSection from './SdlMetadataSection'
import ConfirmationModal from './ConfirmationModal'
import { getTenantUrl } from '../lib/utils'
import { apiClient, BulkImportResponse } from '../lib/api'
import AIInsightsDrawer from './AIInsightsDrawer';
import DashboardAssistantBar from './DashboardAssistantBar';

// ── AI Assistant suggestions ─────────────────────────────────────────

const ENTITY_MODEL_SUGGESTIONS = [
  'What are my strongest competitive differentiators?',
  'Which audience segments am I underserving?',
  'What common objections should my content address?',
  'Where are the gaps in my entity model?',
];

interface SDLAgentTabProps {
  tenantId: string
  subdomain: string
}

type SubTab = 'entity_model' | 'embeddings' | 'question_templates'

export default function SDLAgentTab({ tenantId, subdomain }: SDLAgentTabProps) {
  const [subTab, setSubTab] = useState<SubTab>('entity_model')
  // AI Assistant chat (ai_authority context)
  const [showAIChat, setShowAIChat] = useState(false);
  const [initialAIMessage, setInitialAIMessage] = useState<string | undefined>(undefined);
  // Whether AI chat should be offered (model shown + active version selected).
  const [chatAvailable, setChatAvailable] = useState(false);
  // Replace-button state surfaced by EntityModel, rendered in the tab row.
  const [replaceState, setReplaceState] = useState<EntityModelReplaceState>({ present: false, uploading: false, open: () => {} });

  const queryClient = useQueryClient()

  // ── Client question state (derived from ai-api — total + LARS-enabled counts) ──
  const { data: counts, refetch: refetchCounts } = useQuery({
    queryKey: ['tenantQuestionCounts', tenantId],
    queryFn: () => apiClient.getTenantQuestionCounts(tenantId),
    enabled: !!tenantId,
  })

  // ── SDL import → populates the tenant's question bank (client admin Question Management) ──
  const importInputRef = useRef<HTMLInputElement>(null)
  const [importing, setImporting] = useState(false)
  const [importError, setImportError] = useState<string | null>(null)
  const [importResult, setImportResult] = useState<BulkImportResponse | null>(null)

  const handleImportFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setImporting(true)
    setImportError(null)
    setImportResult(null)
    try {
      const json = JSON.parse(await file.text())
      const res = await apiClient.importQuestionsToEntityModel(tenantId, json)
      setImportResult(res)
      refetchCounts()
      queryClient.invalidateQueries({ queryKey: ['tenantSdlMetadata', tenantId] })
    } catch (err: any) {
      setImportError(err.message ?? 'Failed to import SDL')
    } finally {
      setImporting(false)
      if (importInputRef.current) importInputRef.current.value = ''
    }
  }

  // ── Push to LARS → rebuilds the SDL from the entity model and syncs the tenant's LARS run ──
  const [pushing, setPushing] = useState(false)
  const [pushError, setPushError] = useState<string | null>(null)
  const [pushMessage, setPushMessage] = useState<string | null>(null)

  const handlePushToLars = async () => {
    setPushing(true)
    setPushError(null)
    setPushMessage(null)
    try {
      const res = await apiClient.pushTenantSdlToLars(tenantId, subdomain)
      setPushMessage(`Pushed ${res.question_count} questions to LARS.`)
    } catch (err: any) {
      setPushError(err.message ?? 'Failed to push SDL to LARS')
    } finally {
      setPushing(false)
    }
  }

  // ── Delete SDL → removes SDL-imported questions + clears all SDL metadata ──
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [deleteMessage, setDeleteMessage] = useState<string | null>(null)

  const handleDeleteSdl = async () => {
    setDeleting(true)
    setDeleteError(null)
    setDeleteMessage(null)
    try {
      const res = await apiClient.deleteTenantSdl(tenantId)
      setDeleteMessage(
        `Deleted ${res.questions_deleted} SDL question${res.questions_deleted === 1 ? '' : 's'}` +
        (res.metadata_cleared ? ' and cleared SDL metadata.' : '.')
      )
      setShowDeleteConfirm(false)
      refetchCounts()
      queryClient.invalidateQueries({ queryKey: ['tenantSdlMetadata', tenantId] })
    } catch (err: any) {
      setShowDeleteConfirm(false)
      setDeleteError(err.message ?? 'Failed to delete SDL')
    } finally {
      setDeleting(false)
    }
  }

  const subTabs: { id: SubTab; label: string; disabled?: boolean }[] = [
    { id: 'entity_model', label: 'Entity Model' },
    { id: 'embeddings', label: 'Embeddings' },
    { id: 'question_templates', label: 'Question Templates', disabled: true },
  ]

  const embeddingsUrl = `${getTenantUrl(subdomain)}/knowledge-base/embeddings`

  // Open the AI Assistant and auto-send a free-form question from the composer.
  const handleAssistantSubmit = useCallback((text: string) => {
    setInitialAIMessage(text);
    setShowAIChat(true);
  }, []);

  return (
    <div>
      {/* pb-36 keeps the last row scrollable clear of the fixed assistant bar */}
      <div className='pb-36'>
        {/* ── Import SDL → Question Management ── */}
        <div className="bg-white border border-gray-100 rounded-xl p-5 mb-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">SDL &amp; LARS</h3>
              <p className="text-sm text-gray-500">
                Import an SDL JSON to populate this tenant's Question Management (client admin) with questions,
                strategic foci, variables &amp; competitors. Push to LARS rebuilds the SDL from this data and syncs
                the tenant's LARS run.
              </p>
              {/* Client question state — total + LARS-enabled, from ai-api */}
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                  {counts ? counts.total : '—'} questions
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
                  {counts ? counts.lars_enabled : '—'} LARS-enabled
                </span>
              </div>
              {importError && (
                <p className="mt-2 flex items-center gap-1.5 text-xs text-red-600">
                  <AlertTriangle className="h-3.5 w-3.5" /> {importError}
                </p>
              )}
              {importResult && (
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                    <CheckCircle2 className="h-3 w-3" />
                    {importResult.total_created} created
                  </span>
                  {importResult.total_skipped > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                      <AlertTriangle className="h-3 w-3" />
                      {importResult.total_skipped} skipped (already exist)
                    </span>
                  )}
                  <span className="text-gray-400">of {importResult.total_submitted} submitted</span>
                </div>
              )}
              {pushError && (
                <p className="mt-2 flex items-center gap-1.5 text-xs text-red-600">
                  <AlertTriangle className="h-3.5 w-3.5" /> {pushError}
                </p>
              )}
              {pushMessage && (
                <p className="mt-2 flex items-center gap-1.5 text-xs text-emerald-700">
                  <CheckCircle2 className="h-3.5 w-3.5" /> {pushMessage}
                </p>
              )}
              {deleteError && (
                <p className="mt-2 flex items-center gap-1.5 text-xs text-red-600">
                  <AlertTriangle className="h-3.5 w-3.5" /> {deleteError}
                </p>
              )}
              {deleteMessage && (
                <p className="mt-2 flex items-center gap-1.5 text-xs text-emerald-700">
                  <CheckCircle2 className="h-3.5 w-3.5" /> {deleteMessage}
                </p>
              )}
            </div>
            <div className="flex flex-row sm:flex-col gap-2 sm:flex-shrink-0">
              <button
                onClick={() => importInputRef.current?.click()}
                disabled={importing}
                className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
              >
                {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadIcon className="h-4 w-4" />}
                {importing ? 'Importing…' : 'Import SDL'}
              </button>
              <button
                onClick={handlePushToLars}
                disabled={pushing || (counts ? counts.lars_enabled === 0 : false)}
                title={counts && counts.lars_enabled === 0 ? 'No LARS-enabled questions to push' : 'Rebuild SDL from the entity model and push to LARS'}
                className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-primary-700 bg-white border border-primary-300 rounded-lg hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
              >
                {pushing ? <Loader2 className="h-4 w-4 animate-spin" /> : <SendIcon className="h-4 w-4" />}
                {pushing ? 'Pushing…' : 'Push to LARS'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={deleting || (counts ? counts.sdl_questions === 0 : false)}
                title={counts && counts.sdl_questions === 0 ? 'No SDL imported for this tenant' : 'Delete SDL-imported questions and clear all SDL metadata'}
                className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-red-700 bg-white border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
              >
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2Icon className="h-4 w-4" />}
                {deleting ? 'Deleting…' : 'Delete SDL'}
              </button>
            </div>
          </div>
          <input ref={importInputRef} type="file" accept=".json,application/json" onChange={handleImportFileChange} className="hidden" />
        </div>
        {/* ── SDL metadata sections — view/edit what the distributable sdl.json is built from ── */}
        <SdlMetadataSection tenantId={tenantId} />
        {/* ── Sub-tab pills + AI Chat ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div className="flex flex-wrap gap-1.5">
            {subTabs.map((tab) => (
              <button
                key={tab.id}
                disabled={tab.disabled}
                onClick={() => !tab.disabled && setSubTab(tab.id)}
                className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors ${tab.disabled
                  ? 'bg-white text-gray-400 border border-gray-200 opacity-80 cursor-not-allowed'
                  : subTab === tab.id
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300 hover:text-gray-800'
                  }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {subTab === 'entity_model' && replaceState.present && <button
            type="button"
            onClick={replaceState.open}
            disabled={replaceState.uploading}
            className="inline-flex items-center justify-center gap-2 w-full sm:w-auto sm:flex-shrink-0 px-4 py-1.5 text-sm font-semibold text-primary-700 bg-white border border-primary-300 rounded-lg hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {replaceState.uploading
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <UploadCloudIcon className="h-4 w-4" />}
            {replaceState.uploading ? 'Uploading…' : 'Replace entity model'}
          </button>}
        </div>
        {/* ── Entity Model sub-area ── */}
        {subTab === 'entity_model' && <EntityModel tenantId={tenantId} onChatAvailableChange={setChatAvailable} onReplaceStateChange={setReplaceState} />}
        {/* ── Embeddings sub-area ── */}
        {subTab === 'embeddings' && (
          <div className="bg-white border border-gray-100 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0">
                <LayersIcon className="h-5 w-5 text-primary-500" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-900 mb-1">Knowledge Base Embeddings</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Manage embeddings for this tenant's knowledge base in the client admin portal.
                </p>
                <a
                  href={embeddingsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Open Embeddings
                  <ExternalLinkIcon className="h-4 w-4" />
                </a>
                <p className="text-xs text-gray-400 mt-3 font-mono break-all">{embeddingsUrl}</p>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Dashboard assistant composer — fixed bottom center */}
      {subTab === 'entity_model' && chatAvailable && (
        <DashboardAssistantBar
          onSubmit={handleAssistantSubmit}
          onOpen={() => setShowAIChat(true)}
          suggestions={ENTITY_MODEL_SUGGESTIONS}
        />
      )}

      {/* Delete SDL confirmation */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => !deleting && setShowDeleteConfirm(false)}
        onConfirm={handleDeleteSdl}
        title="Delete SDL"
        message={`This will permanently delete ${counts ? counts.sdl_questions : 'all'} SDL-imported question${counts?.sdl_questions === 1 ? '' : 's'} (and their embeddings) and clear all SDL metadata sections — strategic foci, variables, competitors, entity model summary, brand & industry. Manually created questions are not affected. This cannot be undone.`}
        confirmText="Delete SDL"
        type="danger"
        isLoading={deleting}
      />

      {/* AI Insights Drawer — ai_authority context */}
      <AIInsightsDrawer
        isOpen={showAIChat}
        onClose={() => { setShowAIChat(false); setInitialAIMessage(undefined); }}
        tenantId={tenantId}
        contextKey="entity_model"
        description="Entity Model context"
        title="Ask me anything about your this Entity Model"
        subtitle="Get insights grounded in your entity model and your own content."
        suggestions={ENTITY_MODEL_SUGGESTIONS}
        initialMessage={initialAIMessage}
      />
    </div>
  )
}
